import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import SaveIcon from '@mui/icons-material/Save';
import {
  AlertColor,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Link,
  List,
  ListItem,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import { saveAs } from 'file-saver';
import html2pdf from 'html2pdf.js';
import JSZip from 'jszip';

import { makeAuthenticatedApiCall } from '../apiHelper';
import { useAuth } from '../components/AuthContext';
import CaseArbitration from '../components/CarDealDetails/CaseArbitration';
import EvoComment from '../components/CarDealDetails/EvoComment';
import { businessTypeOptions } from '../constants/cotation';
import { useOrganizations } from '../hooks/useApiQueries';
import { CarDeal } from '../types/deal';
import FileCarouselDialog from '../components/FileCarouselDialog';
import { Capitalize, formatDamages, formatDateToDDMMYYYY, formatKilometers, priceFormatter } from '../utils';
import { isPdf } from '../utils';
import skipcarLogoBlack from '../assets/logo_skipcar_black.png';

const CarDealDetailsPage: React.FC = () => {
  const [deal, setDeal] = useState<CarDeal | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState<number>(0);
  const [allPhotos, setAllPhotos] = useState<string[]>([]);
  const [validPhotos, setValidPhotos] = useState<Record<string, string[]>>({});
  const [filteredPhotos, setFilteredPhotos] = useState<Record<string, string[]>>({});

  const [isEvoCommenting, setIsEvoCommenting] = useState<boolean>(false);
  const [isAgencyResponding, setIsAgencyResponding] = useState<boolean>(false);

  const [doubleCotation, setDoubleCotation] = useState({ dcMarketPrice: '', dcDesiredPrice: '' });

  const { data: organizations, isLoading: isLoadingOrganizations } = useOrganizations();

  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('common');

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const make = queryParams.get('make');
  const fuel = queryParams.get('fuel');
  const prospection = queryParams.get('prospection');

  const { currentUser } = useAuth();
  const userRole = currentUser?.data?.data?.role;
  const isArbitrator = queryParams.has('arbitrator') && (userRole === 'god' || userRole === 'arbitrator');

  const [selectedOrgId, setSelectedOrgId] = useState<number | string>('');
  const [selectedOrgLabel, setSelectedOrgLabel] = useState<string>('');

  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [severity, setSeverity] = useState<AlertColor>('success');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);

  const openSnackbar = (message: string, severity: AlertColor) => {
    setSnackbarMessage(message);
    setSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    axios
      .get(`/api/deals/${id}`, {
        params: {
          make,
          fuel,
          prospection,
        },
      })
      .then((response) => {
        setDeal(response.data);
        setSelectedOrgId(response.data.org_id);
        setLoading(false);
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          setError('Paramètres invalides');
        } else {
          console.error('Erreur lors de la récupération du deal:', error);
          setError(error.message || 'Erreur lors de la récupération des données');
        }
        setLoading(false);
      });
  }, [id]);

  // Remove duplicate organizations, puis tri alphabétique : la liste arrivait dans l'ordre
  // des org_id, donc illisible dès quelques centaines d'agences (on ne peut pas taper pour
  // chercher dans un menu déroulant MUI).
  // `localeCompare` avec sensitivity 'base' pour que « EWIGO NARBONNE » et « Ewigo Aubagne »
  // se rangent ensemble malgré la casse et les accents.
  const uniqueOrganizations = organizations
    ? Array.from(new Set(organizations.map((org: Record<string, any>) => org.org_id)))
        .map((id) => organizations.find((org: Record<string, any>) => org.org_id === id))
        .sort((a: Record<string, any>, b: Record<string, any>) =>
          String(a?.org_name ?? '').localeCompare(String(b?.org_name ?? ''), 'fr', { sensitivity: 'base' }),
        )
    : [];

  useEffect(() => {
    const fetchDoubleCotation = async () => {
      try {
        const response = await axios.get(`/api/deals/double-cotation/${id}`);
        setDoubleCotation({
          dcMarketPrice: response.data.dc_market_price,
          dcDesiredPrice: response.data.dc_desired_price,
        });
      } catch (error) {
        console.error('Failed to fetch double cotation data', error);
      }
    };

    fetchDoubleCotation();
  }, [id]);

  // Gestionnaire d'erreur pour les images
  const handleImageError = (category: string, url: string) => {
    setValidPhotos((prevValidPhotos) => {
      // Créer une copie de l'état actuel pour éviter de modifier directement l'état
      const updatedPhotos = { ...prevValidPhotos };
      // Filtrer l'URL qui a causé une erreur
      updatedPhotos[category] = updatedPhotos[category].filter((photoUrl) => photoUrl !== url);
      return updatedPhotos;
    });
  };

  const generatePDF = () => {
    const content = document.getElementById('car-details-content');
    if (content) {
      html2pdf(content, {
        margin: 10,
        filename: `Skipcar-Fiche-${deal?.pipedrive_deal_id}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 3 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      });
    }
  };

  const getDepositSaleSoldPictures = async () => {
    const response = await makeAuthenticatedApiCall('get', `/api/misiv/deposit-sale-buyer/${deal?.pipedrive_deal_id}`);
    return (response.data as { files: string[] }).files;
  };

  const fetchDepositSaleSoldPictures = async () => {
    if (deal && deal?.business_type === 'deposit_sale' && userRole === 'god') {
      const depositSaleSoldPictures = await getDepositSaleSoldPictures();
      return depositSaleSoldPictures;
    }
    return [];
  };

  const filterPhotosForRole = (photos: {
    [x: string]: any[] | string[];
    certificateOfTransfer?: any;
    salesCertificate?: any;
    barredVehicleRegistrationDocument?: any;
    gloveBoxDocuments?: any;
    signedPurchaseDeclaration?: any;
    signedPurchaseDeclarationDepositSale?: any;
    signedCessionCertificate?: any;
  }) => {
    if (userRole !== 'god') {
      delete photos.certificateOfTransfer;
      delete photos.salesCertificate;
      delete photos.barredVehicleRegistrationDocument;
      delete photos.gloveBoxDocuments;
      delete photos.signedPurchaseDeclaration;
      delete photos.signedPurchaseDeclarationDepositSale;
      delete photos.signedCessionCertificate;
    } else {
      // Pipeline A: Dossier d'achat Skipcar
      const procurementFilePhotos = [
        ...(photos.certificateOfTransfer || []),
        ...(photos.salesCertificate || []),
        ...(photos.barredVehicleRegistrationDocument || []),
        ...(photos.gloveBoxDocuments || []),
      ];
      photos['procurementFilePhotos'] = procurementFilePhotos;
      delete photos.certificateOfTransfer;
      delete photos.salesCertificate;
      delete photos.barredVehicleRegistrationDocument;
      delete photos.gloveBoxDocuments;

      // Pipeline B: Dossier de vente Skipcar
      const mandateProcurementFilePhotos = [
        ...(photos.signedPurchaseDeclaration || []),
        ...(photos.signedCessionCertificate || []),
      ];
      photos['mandateProcurementFilePhotos'] = mandateProcurementFilePhotos;
      delete photos.signedPurchaseDeclaration;
      delete photos.signedCessionCertificate;
    }
    return photos;
  };

  useEffect(() => {
    if (deal) {
      let filteredPhotos = { ...deal.photos };

      filteredPhotos = filterPhotosForRole(filteredPhotos);

      fetchDepositSaleSoldPictures().then((depositSaleSoldPictures) => {
        filteredPhotos['depositSaleSoldPictures'] = depositSaleSoldPictures;
        setValidPhotos(filteredPhotos);
        setFilteredPhotos(filteredPhotos);

        const photosArray = Object.values(filteredPhotos || {}).flat();
        setAllPhotos(photosArray);
      });
    }
    if (queryParams.has('datastore') && queryParams.get('datastore')) {
      if (deal?.spark_fds_encrypt === queryParams.get('datastore')) {
        setIsAgencyResponding(true);
      } else if (queryParams.get('datastore') === 'evo') {
        setIsEvoCommenting(true);
      }
    }
  }, [deal, userRole]);

  const handleSave = async () => {
    await downloadPhotos();
  };

  const downloadPhotos = async () => {
    const zip = new JSZip();
    const folder = zip.folder(`Skipcar-Fiche-${deal?.pipedrive_deal_id}`);
    const promises = allPhotos.map(async (url, index) => {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const format = blob.type.split('/')[1];
        const fileName = `Skipcar-Fiche-${deal?.pipedrive_deal_id}-${index}.${format}`;
        folder?.file(fileName, blob);
      } catch (error) {
        console.error('Error handling photo:', error);
      }
    });

    await Promise.all(promises);

    try {
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `Skipcar-Fiche-${deal?.pipedrive_deal_id}.zip`);
    } catch (error) {
      console.error('Error generating ZIP:', error);
    }
  };

  const handleOpen = (photoUrl: string) => {
    const photosArray = Object.values(deal?.photos || {}).flat();
    setAllPhotos(photosArray);
    setCurrentPhotoIndex(photosArray.indexOf(photoUrl));
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  function extractFileNameFromUrl(url: string) {
    const urlParts = url.split('/');
    const lastPart = urlParts[urlParts?.length - 1];
    if (lastPart) {
      const fileNameSection = lastPart.split('?')[0];
      const encodedFileName = fileNameSection.split('%2F').pop();
      if (encodedFileName) {
        return decodeURIComponent(encodedFileName);
      }
    }
    return ''; // Retourne une chaîne vide si l'URL n'est pas conforme ou si le nom du fichier est introuvable
  }

  const handleBusinessTypeChange = async (event: SelectChangeEvent<string>) => {
    const newBusinessType = event.target.value as string;
    const selectedOption = businessTypeOptions.find((option) => option.value === newBusinessType);
    if (selectedOption) {
      setDeal((prevDeal) => {
        if (prevDeal) {
          return {
            ...prevDeal,
            business_type: selectedOption.value,
          };
        }
        return prevDeal;
      });
      try {
        await makeAuthenticatedApiCall('post', `/api/deals/update-business-type`, {
          params: {
            pipedriveDealId: deal?.pipedrive_deal_id,
            businessType: selectedOption.value,
          },
        });
        console.log('Business type updated successfully');
      } catch (error) {
        console.error('Error updating business type:', error);
      }
    }
  };

  const handleOrganizationChange = async (newOrgId: string, newOrgLabel: string) => {
    setSelectedOrgId(newOrgId);
    setSelectedOrgLabel(newOrgLabel);

    try {
      await makeAuthenticatedApiCall('post', '/api/deals/update-org-id', {
        params: {
          pipedriveDealId: deal?.pipedrive_deal_id,
          orgId: newOrgId,
          orgName: newOrgLabel,
        },
      });
      console.log('Organization updated successfully');
      openSnackbar('Organisation mise à jour avec succès', 'success');
    } catch (error) {
      console.error('Error updating organization:', error);
      openSnackbar("Erreur lors de la mise à jour de l'organisation", 'error');
    }
  };

  if (loading) {
    return <CircularProgress style={{ alignContent: 'center' }} size={10} />;
  }

  if (error) {
    return (
      <Container>
        <Paper elevation={3} style={{ padding: '16px' }}>
          <Typography variant="body1">Erreur : {error}</Typography>
          <Typography variant="body2">Vous n'avez probablement pas l'autorisation d'accéder à cette page</Typography>
          <Typography variant="body2">
            Si vous pensez que c'est une erreur, veuillez contacter le{' '}
            <Link target="_blank" href="mailto:luc@aptekaa.fr">
              support technique
            </Link>
            .
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container>
      {deal && (
        <>
          <Paper id="car-details-content" elevation={3} style={{ padding: '16px' }}>
            <Button
              onClick={generatePDF}
              style={{
                position: 'relative',
                float: 'right',
                right: '0px',
                top: '0px',
                backgroundColor: theme.palette.saffron.main,
                zIndex: 1000,
              }}
            >
              <PrintIcon color="action" />
            </Button>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2">ID : {deal.pipedrive_deal_id}</Typography>
                {deal.acceptation_date && (
                  <Typography variant="body2">Acceptée le : {formatDateToDDMMYYYY(deal.acceptation_date)}</Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <img src={skipcarLogoBlack} style={{ float: 'right', width: '150px' }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body1">{deal.registration_plate}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h5" align="right">
                  {deal.buy_back_date.replace(/-/g, '/')}
                </Typography>
                {(userRole === 'god' || userRole === 'arbitrator') && (
                  <>
                    <Box mt={2}>
                      {deal.purchase_price && (
                        <Typography variant="h5" align="right">
                          Prix d'achat : {priceFormatter.format(parseInt(deal.purchase_price))}
                        </Typography>
                      )}
                      {deal.encheres_vo_price && (
                        <Typography variant="h5" align="right">
                          Prix Enchères VO : {priceFormatter.format(parseInt(deal.encheres_vo_price))}
                        </Typography>
                      )}
                      {deal.final_purchase_price && (
                        <Typography variant="h5" align="right">
                          Prix d'achat (Final) : {priceFormatter.format(parseInt(deal.final_purchase_price))}
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </Grid>
              {isArbitrator && (
                <CaseArbitration
                  dealId={deal.pipedrive_deal_id}
                  extUrl={deal.ext_url}
                  purchasePrice={deal.purchase_price ? priceFormatter.format(parseInt(deal.purchase_price)) : ''}
                  encheresVoPrice={
                    deal.encheres_vo_price ? priceFormatter.format(parseInt(deal.encheres_vo_price)) : ''
                  }
                />
              )}
              <Divider
                variant="fullWidth"
                component="hr"
                style={{
                  marginLeft: 24,
                  backgroundColor: '#fdd15f',
                  height: '2px',
                  width: 'calc(100% - 24px)',
                  border: 0,
                  marginTop: 15,
                }}
              />
              <Grid item xs={12} md={12}>
                <Typography variant="body2">{deal.make ? Capitalize(deal.make.replace(/_/g, ' ')) : ''}</Typography>
              </Grid>
              <Grid item xs={12} md={12}>
                <Typography variant="body2">
                  {deal.model} {deal.derivative} {deal.commercial_finishing}
                </Typography>
              </Grid>

              <Divider
                variant="fullWidth"
                component="hr"
                style={{
                  marginLeft: 24,
                  backgroundColor: '#fdd15f',
                  height: '2px',
                  width: 'calc(100% - 24px)',
                  border: 0,
                  marginTop: 15,
                }}
              />
              <Grid item xs={12} md={12}>
                <Typography variant="body1" fontSize={22}>
                  Informations générales
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">Agence responsable :</Typography>
                {userRole === 'agency' || !userRole ? (
                  <Typography variant="body2">{deal.org_name}</Typography>
                ) : !isLoadingOrganizations || !userRole ? (
                  <Select
                    labelId="agency-label"
                    label="Agence responsable"
                    className="inputElement"
                    sx={{ height: '40px' }}
                    value={selectedOrgId?.toString()}
                    onChange={(event) => {
                      const newOrgId = event.target.value;
                      const newOrgName = uniqueOrganizations.filter((org) => org?.org_id === newOrgId)[0]?.org_name;
                      handleOrganizationChange(newOrgId, newOrgName);
                    }}
                  >
                    {uniqueOrganizations.map((org) => (
                      <MenuItem key={org?.org_id} value={org?.org_id}>
                        {org?.org_name}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <CircularProgress />
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">Type d'affaire :</Typography>
                {userRole === 'agency' || !userRole ? (
                  <Typography variant="body2">{t(`business_type.${deal.business_type}`)}</Typography>
                ) : (
                  <Select
                    labelId="business_type-label"
                    label="business_type"
                    className="inputElement"
                    sx={{ height: '40px' }}
                    onChange={handleBusinessTypeChange}
                    value={deal.business_type}
                  >
                    {businessTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">Genre :</Typography>
                <Typography variant="body2">{deal.genre ? Capitalize(deal.genre) : ''}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">Date d'immatriculation :</Typography>
                <Typography variant="body2">{formatDateToDDMMYYYY(deal.reg_date)}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">Carburant :</Typography>
                <Typography variant="body2">{t(`fuel.${deal.fuel}`)}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">Transmission :</Typography>
                <Typography variant="body2">{t(`transmission.${deal.transmission}`)}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">Couleur :</Typography>
                <Typography variant="body2">{t(`colour.${deal.colour}`)}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">Portes :</Typography>
                <Typography variant="body2">{deal.number_of_doors}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">Kilométrage compteur :</Typography>
                <Typography variant="body2">{formatKilometers(deal.kms)}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">Confirmation kilométrage :</Typography>
                <Typography variant="body2">{deal.kms_confirm ? 'Oui' : 'Non'}</Typography>
              </Grid>
              {deal.location && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body1">Localisation :</Typography>
                  <Typography variant="body2">{deal.location}</Typography>
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <Typography variant="body1">Places :</Typography>
                <Typography variant="body2">{deal.number_of_seats}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">VIN :</Typography>
                <Typography variant="body2">{deal.vin}</Typography>
              </Grid>
              <Divider
                variant="fullWidth"
                component="hr"
                style={{
                  marginLeft: 24,
                  backgroundColor: '#fdd15f',
                  height: '2px',
                  width: 'calc(100% - 24px)',
                  border: 0,
                  marginTop: 15,
                }}
              />
              <Grid item xs={12} md={12}>
                <Typography variant="body1" fontSize={22}>
                  Informations complémentaires
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body1">Carnet d'entretien :</Typography>
                <Typography variant="body2">{t(`documentation.${deal.documentation}`)}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">Double des clés :</Typography>
                <Typography variant="body2">
                  {deal.duplicate_keys ? t(`duplicateKeys.${deal.duplicate_keys}`) : 'Non'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">TVA :</Typography>
                <Typography variant="body2">{deal.vat ? 'Oui' : 'Non'}</Typography>
              </Grid>
              {deal.date_last_revision && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body1">Date de dernière révision :</Typography>
                  <Typography variant="body2">
                    {deal.date_last_revision ? deal.date_last_revision.replace(/-/g, '/') : 'N/C'}
                  </Typography>
                </Grid>
              )}
              {deal.kms_last_revision && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body1">Kilométrage dernière révision :</Typography>
                  <Typography variant="body2">{formatKilometers(deal.kms_last_revision)}</Typography>
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <Typography variant="body1">Origine du véhicule :</Typography>
                <Typography variant="body2">{t(`source.${deal.source}`)}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">Véhicule d'occasion :</Typography>
                <Typography variant="body2"> {deal.source === 'second_hand_french' ? 'Oui' : 'Non'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body1">Type de véhicule :</Typography>
                <Typography variant="body2">{t(`vehicle_type.${deal.vehicle_type}`)}</Typography>
              </Grid>
              <Divider
                variant="fullWidth"
                component="hr"
                style={{
                  marginLeft: 24,
                  backgroundColor: '#fdd15f',
                  height: '2px',
                  width: 'calc(100% - 24px)',
                  border: 0,
                  marginTop: 15,
                }}
              />
              <Grid item xs={12} md={6}>
                <Typography variant="body1" fontSize={22}>
                  Dommages
                </Typography>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ marginBottom: 3 }}>
                    Coût des dommages : {deal.damages_cost} €
                  </Typography>
                </Grid>
                <Typography variant="body2">Dommages relevés par outil EVO :</Typography>
                <List>
                  {formatDamages(deal?.damages).map((damage: string, index: number) => (
                    <ListItem key={index}>
                      <span style={{ marginRight: '10px' }}>&bull;</span>
                      {damage}
                    </ListItem>
                  ))}
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">Photos :</Typography>
                <Button
                  onClick={handleSave}
                  style={{
                    position: 'relative',
                    float: 'right',
                    right: '0px',
                    top: '0px',
                    backgroundColor: theme.palette.saffron.main,
                    zIndex: 1000,
                    cursor: 'pointer',
                  }}
                >
                  <SaveIcon color="action" />
                </Button>

                <Grid>
                  {Object.entries(filteredPhotos).map(([category]) => {
                    const hasValidPhotos = validPhotos[category] && validPhotos[category]?.length > 0;
                    return hasValidPhotos ? (
                      <div key={category}>
                        <Typography variant="body2" sx={{ marginTop: 2 }}>
                          {t(`carDealPictures.${category}`)}
                        </Typography>
                        <Box sx={{ display: 'flex', flexFlow: 'row wrap' }}>
                          {validPhotos[category].map((photoUrl, index) => {
                            if (isPdf(photoUrl)) {
                              // Gestion des fichiers PDF
                              return (
                                <Grid
                                  style={{
                                    height: 130,
                                    overflow: 'hidden',
                                    objectPosition: 'center',
                                    objectFit: 'cover',
                                    marginBottom: 10,
                                    background: 'rgba(253, 187, 95, 0.4)',
                                    margin: '6px 8px',
                                    width: 100,
                                  }}
                                  item
                                  xs={12}
                                  sm={6}
                                  md={4}
                                  key={index}
                                >
                                  <Button
                                    onClick={() => handleOpen(photoUrl)}
                                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                                  >
                                    <PictureAsPdfIcon
                                      style={{
                                        fontSize: 10,
                                        height: 'auto',
                                        width: '70px',
                                        color: 'black',
                                        marginTop: '10px',
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        fontFamily: 'Urbanist',
                                        marginTop: '-5px',
                                        fontSize: '8px',
                                        textDecoration: 'none',
                                        color: 'black',
                                        textAlign: 'center',
                                      }}
                                    >
                                      {extractFileNameFromUrl(photoUrl)}
                                    </Typography>
                                  </Button>
                                </Grid>
                              );
                            } else {
                              // Gestion des images normales
                              return (
                                <Grid
                                  style={{
                                    minWidth: 150,
                                    height: 130,
                                    overflow: 'hidden',
                                    objectPosition: 'center',
                                    objectFit: 'cover',
                                    marginBottom: 10,
                                  }}
                                  item
                                  xs={12}
                                  sm={6}
                                  md={4}
                                  key={index}
                                >
                                  <Button onClick={() => handleOpen(photoUrl)}>
                                    <img
                                      src={photoUrl}
                                      alt={`${category} Photo ${index}`}
                                      style={{ width: '100%', cursor: 'pointer' }}
                                      onError={() => handleImageError(category, photoUrl)}
                                    />
                                  </Button>
                                </Grid>
                              );
                            }
                          })}
                        </Box>
                      </div>
                    ) : null;
                  })}
                </Grid>
              </Grid>
              <Divider
                variant="fullWidth"
                component="hr"
                style={{
                  marginLeft: 24,
                  backgroundColor: '#fdd15f',
                  height: '2px',
                  width: 'calc(100% - 24px)',
                  border: 0,
                  marginTop: 15,
                }}
              />
              <Grid item xs={12} md={12}>
                <Typography variant="body1" fontSize={22}>
                  Essai du véhicule
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">Essai routier :</Typography>
                <Typography variant="body2">{t(`test_drive.${deal.test_drive}`)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">Condition mécanique :</Typography>
                <Typography variant="body2">{t(`mechanical_condition.${deal.mechanical_condition}`)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">Conditions d'expertise :</Typography>
                <Typography variant="body2">{t(`conditions_expertise.${deal.conditions_expertise}`)}</Typography>
              </Grid>
              {deal.comp_infos_1 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body1">Infos complémentaires 1 :</Typography>
                  <Typography variant="body2">{deal.comp_infos_1}</Typography>
                </Grid>
              )}
              <Divider
                variant="fullWidth"
                component="hr"
                style={{
                  marginLeft: 24,
                  backgroundColor: '#fdd15f',
                  height: '2px',
                  width: 'calc(100% - 24px)',
                  border: 0,
                  marginTop: 15,
                }}
              />
              <Grid item xs={12} md={12}>
                <Typography variant="body1" fontSize={22}>
                  Autres informations
                </Typography>
              </Grid>
              {deal.desired_price && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body1">Prix souhaité par le client :</Typography>
                  <Typography variant="body2">{priceFormatter.format(parseInt(deal.desired_price))}</Typography>
                </Grid>
              )}
              {deal.market_price && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body1">Prix du marché selon vous :</Typography>
                  <Typography variant="body2">{priceFormatter.format(parseInt(deal.market_price))}</Typography>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Typography variant="body1">Date de reprise :</Typography>
                <Typography variant="body2">{deal.buy_back_date.replace(/-/g, '/')}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">Franchise :</Typography>
                <Typography variant="body2">{deal.franchise}</Typography>
              </Grid>
              {deal.options && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body1">Options :</Typography>
                  <Typography variant="body2">{deal.options}</Typography>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Typography variant="body1">Utilisation du véhicule :</Typography>
                <Typography variant="body2">{t(`vehicle_usage.${deal.vehicle_usage}`)}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">Durée de détention :</Typography>
                <Typography variant="body2">{t(`detention_duration.${deal.detention_duration}`)}</Typography>
              </Grid>
              {deal.histovec_link && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body1">Lien Histovec :</Typography>
                  <Link target="_blanl" href={deal.histovec_link} variant="body2">
                    Cliquez ici
                  </Link>
                </Grid>
              )}
              {deal.comp_infos_2 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body1">Infos complémentaires Skipcar :</Typography>
                  <Typography variant="body2">{deal.comp_infos_2}</Typography>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Typography variant="body1">Informations complémentaires (API) :</Typography>
                <Typography variant="body2">{deal.more_api_infos}</Typography>
              </Grid>

              {deal.sales_person_contact && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body1">Contact du commercial :</Typography>
                  <Typography variant="body2">{deal.sales_person_contact}</Typography>
                </Grid>
              )}
              {(userRole === 'god' || userRole === 'arbitrator') && deal.seller_email && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body1">Contact du client vendeur :</Typography>
                  <Typography variant="body2">{deal.seller_email}</Typography>
                </Grid>
              )}

              <Grid item xs={12} md={6} sx={{ display: 'none' }}>
                <Typography variant="body1">URL externe :</Typography>
                <Typography variant="body2">{deal.ext_url}</Typography>
              </Grid>

              {(doubleCotation.dcMarketPrice || doubleCotation.dcDesiredPrice) && (
                <>
                  <Divider
                    variant="fullWidth"
                    component="hr"
                    style={{
                      marginLeft: 24,
                      backgroundColor: '#fdd15f',
                      height: '2px',
                      width: 'calc(100% - 24px)',
                      border: 0,
                      marginTop: 15,
                    }}
                  />
                  <Grid item xs={12} md={12}>
                    <Typography variant="body1" fontSize={22}>
                      Double Cotation
                    </Typography>
                  </Grid>
                </>
              )}
              {doubleCotation.dcMarketPrice && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body1">Prix du marché</Typography>
                  <Typography variant="body2">
                    {priceFormatter.format(parseInt(doubleCotation.dcMarketPrice))}
                  </Typography>
                </Grid>
              )}
              {doubleCotation.dcDesiredPrice && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body1">Prix désiré par le client </Typography>
                  <Typography variant="body2">
                    {priceFormatter.format(parseInt(doubleCotation.dcDesiredPrice))}
                  </Typography>
                </Grid>
              )}

              <EvoComment deal={deal} userType={isAgencyResponding ? 'agency' : isEvoCommenting ? 'evo' : 'other'} />
            </Grid>
          </Paper>
        </>
      )}

      {/* Lightbox/Dialog for photos and PDFs */}
      <FileCarouselDialog allFiles={allPhotos} initialIndex={currentPhotoIndex} open={open} onClose={handleClose} />
    </Container>
  );
};

export default CarDealDetailsPage;
